// People on the screen: the list (all of the person's people, narrowed on the
// screen as typed), one person, and every write.
//
// Promise: a write is shown at once on the person and on the list rows, and
// put back when the server refuses it; the server's answer then replaces the
// person. A note that lost to a newer one shows the newer one.
import {
  type InfiniteData,
  infiniteQueryOptions,
  keepPreviousData,
  type QueryClient,
  type QueryKey,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  PersonDetail,
  PersonExternalKind,
  PersonLabelRef,
  PersonList,
  PersonSummary,
} from "@trameria/core/persons-shared";
import { useApi } from "../app/context";
import { type Api, ApiError, idempotencyKeyFor } from "../data/api";
import { newOptimisticId } from "../data/optimistic";

export const personsPrefix = ["me", "persons"] as const;
const listsPrefix = [...personsPrefix, "list"] as const;
export const personKey = (id: string) =>
  [...personsPrefix, "detail", id] as const;

export const personsQuery = (api: Api, workspace: string | null) =>
  queryOptions({
    queryKey: [...listsPrefix, workspace ?? ""] as const,
    queryFn: () =>
      api<PersonList>(
        `/api/me/persons${workspace === null ? "" : `?workspace=${encodeURIComponent(workspace)}`}`,
      ),
  });

/** A page of the People list (read on as it is scrolled). */
const PERSON_PAGE = 100;
/** The matches of typed words, in one answer (the server's most). */
const PERSON_MATCHES = 500;

/**
 * The People list, page by page: the Workspace of Me, and the words typed above
 * it (the server finds a match not read yet: name, alias, one-line note or
 * external id).
 */
export const personPagesQuery = (
  api: Api,
  workspace: string | null,
  q: string | undefined,
) =>
  infiniteQueryOptions({
    queryKey: [...listsPrefix, "pages", workspace ?? "", q ?? ""] as const,
    queryFn: ({ pageParam }) => {
      // With words the server answers once (no next page): the most it gives.
      const params = new URLSearchParams({
        limit: String(q ? PERSON_MATCHES : PERSON_PAGE),
      });
      if (workspace !== null) params.set("workspace", workspace);
      if (q) params.set("q", q);
      if (pageParam) params.set("cursor", pageParam);
      return api<PersonList>(`/api/me/persons?${params}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
  });

export const personQuery = (api: Api, id: string) =>
  queryOptions({
    queryKey: personKey(id),
    queryFn: () =>
      api<PersonDetail>(`/api/me/persons/${encodeURIComponent(id)}`),
  });

export interface PersonPatch {
  name?: string;
  summary?: string;
  aliases?: string[];
  note?: string;
  note_revision?: number;
  add_external_ids?: {
    kind: PersonExternalKind;
    scope?: string;
    value: string;
  }[];
  remove_external_ids?: string[];
  /** The whole set of labels (ids). */
  labels?: string[];
  /** The account this person is (null: none). Only where the host can link. */
  linked_user_id?: string | null;
}

export function applyPersonPatch(
  p: PersonDetail,
  patch: PersonPatch,
  labelRefs: readonly PersonLabelRef[] = [],
): PersonDetail {
  const out: PersonDetail = { ...p };
  if (patch.name !== undefined) out.name = patch.name;
  if (patch.summary !== undefined) out.summary = patch.summary;
  if (patch.aliases !== undefined) out.aliases = patch.aliases;
  if (patch.note !== undefined) out.note = patch.note;
  if (patch.linked_user_id !== undefined)
    out.linkedUserId = patch.linked_user_id;
  if (patch.labels !== undefined)
    out.labels = patch.labels.map(
      (id) =>
        labelRefs.find((l) => l.id === id) ??
        p.labels.find((l) => l.id === id) ?? { id, name: "" },
    );
  if (patch.remove_external_ids)
    out.externalIds = out.externalIds.filter(
      (x) => !patch.remove_external_ids?.includes(x.id),
    );
  if (patch.add_external_ids)
    out.externalIds = [
      ...out.externalIds,
      ...patch.add_external_ids.map((x) => ({
        id: newOptimisticId(),
        kind: x.kind,
        scope: x.scope ?? "",
        value: x.value.trim(),
        url: null,
      })),
    ];
  return out;
}

const summaryOf = (p: PersonDetail): PersonSummary => ({
  id: p.id,
  name: p.name,
  aliases: p.aliases,
  summary: p.summary,
  linkedUserId: p.linkedUserId,
  externalIds: p.externalIds,
  labels: p.labels,
  workspaceIds: p.workspaceIds,
  waitingCount: p.waitingCount,
  lastTouchedAt: p.lastTouchedAt,
  createdAt: p.createdAt,
  updatedAt: p.updatedAt,
});

/** A list in the cache: one answer, or the pages of the People screen. */
type ListData = PersonList | InfiniteData<PersonList, string | null>;

interface Snapshot {
  detail: PersonDetail | undefined;
  lists: [QueryKey, ListData | undefined][];
}

async function snapshot(qc: QueryClient, id: string): Promise<Snapshot> {
  await qc.cancelQueries({ queryKey: personKey(id) });
  await qc.cancelQueries({ queryKey: listsPrefix });
  return {
    detail: qc.getQueryData<PersonDetail>(personKey(id)),
    lists: qc.getQueriesData<ListData>({ queryKey: listsPrefix }),
  };
}

function restore(qc: QueryClient, id: string, s: Snapshot | undefined) {
  if (!s) return;
  qc.setQueryData(personKey(id), s.detail);
  for (const [key, value] of s.lists) qc.setQueryData(key, value);
}

/** Rewrites the rows of every list in the cache (a new one goes on the first page). */
const mapLists = (
  qc: QueryClient,
  fn: (items: PersonList["items"]) => PersonList["items"],
) =>
  qc.setQueriesData<ListData>({ queryKey: listsPrefix }, (old) =>
    !old
      ? old
      : "pages" in old
        ? {
            ...old,
            pages: old.pages.map((p, i) =>
              i === 0
                ? { ...p, items: fn(p.items) }
                : {
                    ...p,
                    // Only rewrite what is there; adding is the first page's.
                    items: fn(p.items).filter((x) =>
                      p.items.some((y) => y.id === x.id),
                    ),
                  },
            ),
          }
        : { ...old, items: fn(old.items) },
  );

const invalidateLists = (qc: QueryClient) =>
  qc.invalidateQueries({ queryKey: listsPrefix });

function staleNoteOf(error: unknown) {
  if (!(error instanceof ApiError) || error.code !== "stale_note") return null;
  const d = error.details as { note?: unknown; noteRevision?: unknown } | null;
  return typeof d?.note === "string" && typeof d.noteRevision === "number"
    ? { note: d.note, noteRevision: d.noteRevision }
    : null;
}

export interface UpdatePersonInput {
  personId: string;
  patch: PersonPatch;
  /** Names of the labels set (the optimistic rows show them). */
  labelRefs?: PersonLabelRef[];
}

export function useUpdatePerson() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePersonInput) =>
      api<PersonDetail>(
        `/api/me/persons/${encodeURIComponent(input.personId)}`,
        { method: "PATCH", body: input.patch },
      ),
    onMutate: async (input: UpdatePersonInput) => {
      const before = await snapshot(qc, input.personId);
      if (before.detail) {
        const after = applyPersonPatch(
          before.detail,
          input.patch,
          input.labelRefs,
        );
        qc.setQueryData(personKey(input.personId), after);
        mapLists(qc, (items) =>
          items.map((x) =>
            x.id === after.id ? { ...x, ...summaryOf(after) } : x,
          ),
        );
      }
      return before;
    },
    onError: (error, input, before) => {
      restore(qc, input.personId, before);
      const stale = staleNoteOf(error);
      if (stale)
        qc.setQueryData<PersonDetail>(
          personKey(input.personId),
          (old) => old && { ...old, ...stale },
        );
    },
    onSuccess: (detail, input) =>
      qc.setQueryData(personKey(input.personId), detail),
    onSettled: () => invalidateLists(qc),
  });
}

export function useCreatePerson() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; summary?: string }) =>
      api<PersonDetail>("/api/me/persons", {
        method: "POST",
        body: input,
        idempotencyKey: idempotencyKeyFor(input),
      }),
    onSuccess: (detail) => {
      qc.setQueryData(personKey(detail.id), detail);
      mapLists(qc, (items) =>
        items.some((x) => x.id === detail.id)
          ? items
          : [summaryOf(detail), ...items],
      );
    },
    onSettled: () => invalidateLists(qc),
  });
}

export function useDeletePerson() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { personId: string; name: string }) =>
      api(`/api/me/persons/${encodeURIComponent(input.personId)}`, {
        method: "DELETE",
      }),
    onMutate: async (input) => {
      const before = await snapshot(qc, input.personId);
      mapLists(qc, (items) => items.filter((x) => x.id !== input.personId));
      return before;
    },
    onError: (_e, input, before) => restore(qc, input.personId, before),
    onSuccess: (_d, input) =>
      qc.removeQueries({ queryKey: personKey(input.personId) }),
    onSettled: () => invalidateLists(qc),
  });
}

/** Merges `fromId` into `keepId`; the screen stays on the person kept. */
export function useMergePerson() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { keepId: string; fromId: string; fromName: string }) =>
      api<PersonDetail>(
        `/api/me/persons/${encodeURIComponent(input.keepId)}/merge`,
        { method: "POST", body: { fromId: input.fromId } },
      ),
    onSuccess: (detail, input) => {
      qc.setQueryData(personKey(detail.id), detail);
      qc.removeQueries({ queryKey: personKey(input.fromId) });
      mapLists(qc, (items) => items.filter((x) => x.id !== input.fromId));
    },
    onSettled: () => invalidateLists(qc),
  });
}

// ---- realtime -------------------------------------------------------------------

export function personChanged(qc: QueryClient, personId: string | undefined) {
  if (personId) void qc.invalidateQueries({ queryKey: personKey(personId) });
  void invalidateLists(qc);
}

export function personDeleted(qc: QueryClient, personId: string | undefined) {
  if (personId) qc.removeQueries({ queryKey: personKey(personId) });
  void invalidateLists(qc);
}

/** A Case changed (waits and history of people may change) or labels changed. */
export const invalidatePersons = (qc: QueryClient) =>
  qc.invalidateQueries({ queryKey: personsPrefix });
